import { supabase } from '../lib/supabase';

// ─── SCHEMA REAL ──────────────────────────────────────────────────────────────
// public.clientes: id, nombre, contacto, categoria, tarifa_sin_emplatado_menor,
//   tarifa_sin_emplatado_mayor, tarifa_emplatado, activo, created_at,
//   nombre_comercial, nif_cif, telefono, email, contacto_principal, direccion,
//   poblacion, provincia, codigo_postal, cliente_sala_corte, cliente_distribucion,
//   tipo_persona, fecha_nacimiento, consentimiento_marketing,
//   fecha_consentimiento_marketing, etiquetas, notas_comerciales, updated_at
//
// public.cliente_areas: id, cliente_id, area (tienda|distribucion|sala_loncheado),
//   activo, fecha_alta, fecha_baja, metadata, created_at, updated_at
//
// public.cliente_canales: id, cliente_id, canal (telefono|whatsapp|email|instagram|facebook|web),
//   identificador_externo, nombre_usuario, principal, verificado, activo, metadata,
//   created_at, updated_at
//
// public.loncheados: id, cliente_id, empleado_id, producto_id, peso_loncheado,
//   precio_cliente, tipo, created_at, peso, emplatado, coste, observaciones,
//   peso_inicial, peso_aprovechado, merma, rendimiento, foto_url,
//   estado_rendimiento, tarifa, coste_sala, lote_interno, lote_externo,
//   numero_blisteres, lote_id, fecha_produccion, trazabilidad_validada,
//   plan_trabajo_id, plan_trabajo_pieza_id, formato_corte
// ─────────────────────────────────────────────────────────────────────────────

// ── LISTADO DE CLIENTES ───────────────────────────────────────────────────────

export async function getClientesConAreas() {
  const { data, error } = await supabase?.from('clientes')?.select(`
      id,
      nombre,
      nombre_comercial,
      tipo_persona,
      telefono,
      email,
      nif_cif,
      activo,
      notas_comerciales,
      updated_at,
      created_at,
      cliente_areas (
        id,
        area,
        activo
      )
    `)?.order('nombre', { ascending: true });

  if (error) {
    console.error('[ComercialClienteService] getClientesConAreas error:', error);
    throw error;
  }
  return data || [];
}

// ── CLIENTE INDIVIDUAL ────────────────────────────────────────────────────────

export async function getClienteById(id) {
  const { data, error } = await supabase?.from('clientes')?.select(`
      id,
      nombre,
      nombre_comercial,
      tipo_persona,
      telefono,
      email,
      nif_cif,
      activo,
      contacto,
      contacto_principal,
      direccion,
      poblacion,
      provincia,
      codigo_postal,
      fecha_nacimiento,
      consentimiento_marketing,
      fecha_consentimiento_marketing,
      notas_comerciales,
      etiquetas,
      updated_at,
      created_at,
      cliente_areas (
        id,
        area,
        activo,
        fecha_alta
      ),
      cliente_canales (
        id,
        canal,
        identificador_externo,
        nombre_usuario,
        principal,
        verificado,
        activo
      )
    `)?.eq('id', id)?.single();

  if (error) {
    console.error('[ComercialClienteService] getClienteById error:', error);
    throw error;
  }
  return data;
}

// ── CREAR CLIENTE ─────────────────────────────────────────────────────────────

export async function crearCliente({ datosGenerales, areas, canales }) {
  // 1. Insertar cliente maestro
  const { data: cliente, error: clienteError } = await supabase?.from('clientes')?.insert({
      nombre: datosGenerales?.nombre,
      nombre_comercial: datosGenerales?.nombre_comercial || null,
      tipo_persona: datosGenerales?.tipo_persona || null,
      nif_cif: datosGenerales?.nif_cif || null,
      telefono: datosGenerales?.telefono || null,
      email: datosGenerales?.email || null,
      direccion: datosGenerales?.direccion || null,
      fecha_nacimiento: datosGenerales?.fecha_nacimiento || null,
      notas_comerciales: datosGenerales?.notas_comerciales || null,
      consentimiento_marketing: datosGenerales?.consentimiento_marketing || false,
      activo: true,
    })?.select('id')?.single();

  if (clienteError) {
    console.error('[ComercialClienteService] crearCliente insert error:', clienteError);
    throw clienteError;
  }

  const clienteId = cliente?.id;

  // 2. Insertar áreas seleccionadas
  if (areas && areas?.length > 0) {
    const areasInsert = areas?.map(area => ({
      cliente_id: clienteId,
      area,
      activo: true,
    }));
    const { error: areasError } = await supabase?.from('cliente_areas')?.insert(areasInsert);
    if (areasError) {
      console.error('[ComercialClienteService] crearCliente areas error:', areasError);
      throw areasError;
    }
  }

  // 3. Insertar canales (teléfono y email si existen)
  const canalesInsert = [];
  if (datosGenerales?.telefono) {
    canalesInsert?.push({
      cliente_id: clienteId,
      canal: 'telefono',
      identificador_externo: datosGenerales?.telefono,
      principal: true,
      activo: true,
    });
  }
  if (datosGenerales?.email) {
    canalesInsert?.push({
      cliente_id: clienteId,
      canal: 'email',
      identificador_externo: datosGenerales?.email,
      principal: !datosGenerales?.telefono,
      activo: true,
    });
  }
  // Canales adicionales pasados explícitamente
  if (canales && canales?.length > 0) {
    canales?.forEach(c => {
      if (c?.identificador_externo) {
        canalesInsert?.push({
          cliente_id: clienteId,
          canal: c?.canal,
          identificador_externo: c?.identificador_externo,
          nombre_usuario: c?.nombre_usuario || null,
          principal: false,
          activo: true,
        });
      }
    });
  }
  if (canalesInsert?.length > 0) {
    const { error: canalesError } = await supabase?.from('cliente_canales')?.insert(canalesInsert);
    if (canalesError) {
      console.error('[ComercialClienteService] crearCliente canales error:', canalesError);
      throw canalesError;
    }
  }

  return clienteId;
}

// ── EDITAR CLIENTE ────────────────────────────────────────────────────────────

export async function editarCliente(id, { datosGenerales, areas }) {
  // 1. Actualizar datos generales
  const { error: updateError } = await supabase?.from('clientes')?.update({
      nombre: datosGenerales?.nombre,
      nombre_comercial: datosGenerales?.nombre_comercial || null,
      tipo_persona: datosGenerales?.tipo_persona || null,
      nif_cif: datosGenerales?.nif_cif || null,
      telefono: datosGenerales?.telefono || null,
      email: datosGenerales?.email || null,
      direccion: datosGenerales?.direccion || null,
      fecha_nacimiento: datosGenerales?.fecha_nacimiento || null,
      notas_comerciales: datosGenerales?.notas_comerciales || null,
      consentimiento_marketing: datosGenerales?.consentimiento_marketing || false,
      updated_at: new Date()?.toISOString(),
    })?.eq('id', id);

  if (updateError) {
    console.error('[ComercialClienteService] editarCliente update error:', updateError);
    throw updateError;
  }

  // 2. Gestionar áreas: eliminar las existentes y reinsertar las nuevas
  const { error: deleteAreasError } = await supabase?.from('cliente_areas')?.delete()?.eq('cliente_id', id);

  if (deleteAreasError) {
    console.error('[ComercialClienteService] editarCliente delete areas error:', deleteAreasError);
    throw deleteAreasError;
  }

  if (areas && areas?.length > 0) {
    const areasInsert = areas?.map(area => ({
      cliente_id: id,
      area,
      activo: true,
    }));
    const { error: areasError } = await supabase?.from('cliente_areas')?.insert(areasInsert);
    if (areasError) {
      console.error('[ComercialClienteService] editarCliente insert areas error:', areasError);
      throw areasError;
    }
  }
}

// ── TOGGLE ACTIVO ─────────────────────────────────────────────────────────────

export async function toggleActivoCliente(id, activo) {
  const { error } = await supabase?.from('clientes')?.update({ activo, updated_at: new Date()?.toISOString() })?.eq('id', id);

  if (error) {
    console.error('[ComercialClienteService] toggleActivoCliente error:', error);
    throw error;
  }
}

// ── CANALES ───────────────────────────────────────────────────────────────────

export async function addCanalCliente(clienteId, { canal, identificador_externo, nombre_usuario }) {
  const { data, error } = await supabase?.from('cliente_canales')?.insert({
      cliente_id: clienteId,
      canal,
      identificador_externo,
      nombre_usuario: nombre_usuario || null,
      activo: true,
    })?.select()?.single();

  if (error) {
    console.error('[ComercialClienteService] addCanalCliente error:', error);
    throw error;
  }
  return data;
}

export async function deleteCanalCliente(canalId) {
  const { error } = await supabase?.from('cliente_canales')?.delete()?.eq('id', canalId);

  if (error) {
    console.error('[ComercialClienteService] deleteCanalCliente error:', error);
    throw error;
  }
}

// ── HISTORIAL SALA (loncheados) ───────────────────────────────────────────────

export async function getLoncheadosByCliente(clienteId) {
  const { data, error } = await supabase?.from('loncheados')?.select(`
      id,
      created_at,
      fecha_produccion,
      tipo,
      peso,
      peso_loncheado,
      rendimiento,
      numero_blisteres,
      formato_corte,
      emplatado,
      observaciones,
      productos (
        id,
        nombre
      )
    `)?.eq('cliente_id', clienteId)?.order('created_at', { ascending: false })?.limit(50);

  if (error) {
    console.error('[ComercialClienteService] getLoncheadosByCliente error:', error);
    throw error;
  }
  return data || [];
}
